// import axios, { type AxiosResponse, type AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { config } from '../config/config';
import { auth } from './UserAuthService';
import { InspectionResultGroupResponse, InspectionLineItemResponse, AddInspectionLineItemResponse } from '@/types/response/InspectionResultResponses';
import { ExpertiseAreasResponse, SitesResponse } from '@/types/response/lookupResponses';
import { TokenResponse, UserPreferencesResponse, LoginResponse, LineItemData } from '@/types/response/userResponses';
import { GetInspectionPlansResponse, GetLineItemsResponse } from '@/types/response/InspectionPlanningResponses';
import { 
  CopyPasteInspectionPlanRequest, 
  CreateGroupRequest, 
  UpdateGroupRequest,
  CreateLineItemRequest, 
  UpdateLineItemRequest,
  UpdateInspectionPlanRequest 
} from '@/types/request/InspectionPlanningRequests';
import {
  SaveUserDetailsRequest,
  SaveUserAssignmentRequest,
  SaveUserPrivilegesRequest,
  ResetUserPasswordRequest,
  SaveUserPreferencesRequest,
} from '@/types/request/userManagementRequests';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';
import {
  ModuleDetailsRequest,
  ReportSettingsRequest,
  TransactionLoggingRequest,
  ScheduleImpactRequest,
  FrequencyUserTypesRequest,
  SaveFrequencyRequest,
  SaveFrequencyProfileRequest,
  DateForwardingRequest,
  SaveAreaRequest,
  SaveSiteRequest,
  ActivateSiteRequest,
  DefaultSiteRequest,
  SaveInProgressRequest,
  ResultsHighlightingRequest,
  SaveClassificationRequest,
  ActivateClassificationRequest,
} from '@/types/request/moduleSettingsRequests';
import {
  OnlineUserResponse,
  LicenseInfoResponse,
  EnterprisePreferencesResponse,
  LoginImageResponse,
  OfflineAppSettingsResponse,
} from '@/types/response/enterpriseResponses';
import {
  SendMessageRequest,
  SaveLicenseInfoRequest,
  SavePasswordPolicyRequest,
  SaveEmailPreferencesRequest,
  DeleteLoginImageRequest,
  SaveLoginImageSelectionRequest,
  SaveSignInMessageRequest,
  SaveOfflineAppSettingsRequest,
  AddModuleRequest,
} from '@/types/request/enterpriseRequests';
import { CorrectiveActionLineItemResponse } from '@/types/response/CorrectiveActionResponses';
import { InspectionScheduleLineItem } from '@/types/response/InspectionScheduleResponses';
import { DashboardToDoResponse } from '@/types/response/TrendAnalysisResponses';

// Custom error classes
class ApiError extends Error {
  constructor(
    public message: string,
    public status?: number,
    public endpoint?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class NetworkError extends Error {
  constructor(message: string = 'Network request failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

// API configuration
const currentMode = import.meta.env.MODE;

let API_BASE_URL = currentMode === 'production'
  ? config.prod.baseUrl
  : currentMode === 'staging'
  ? config.staging.baseUrl
  : config.dev.baseUrl;

let refreshTokenRequest: Promise<void> | null = null;
const pathPrefix = '/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for adding auth tokens, etc.
apiClient.interceptors.request.use(
  (config) => {
    // Add authorization header if token exists
    const token = auth.getToken() || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await refreshToken();
        const token = auth.getToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        auth.logOut();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Development logging
// if (isDevelopment) {
//   apiClient.interceptors.request.use(request => {
//     // console.log('🚀 API Request:', {
//     //   method: request.method?.toUpperCase(),
//     //   url: request.url,
//     //   data: request.data
//     // });
//     return request;
//   });

//   apiClient.interceptors.response.use(
//     response => {
//       // console.log('✅ API Response:', {
//       //   status: response.status,
//       //   url: response.config.url,
//       //   data: response.data
//       // });
//       return response;
//     },
//     error => {
//       console.error('❌ API Error:', {
//         status: error.response?.status,
//         url: error.config?.url,
//         message: error.message
//       });
//       return Promise.reject(error);
//     }
//   );
// }

// Token refresh functionality
const refreshToken = (): Promise<void> => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = makeRefreshTokenRequest();
    refreshTokenRequest.then(resetRefreshTokenRequest, resetRefreshTokenRequest);
  }
 
  return refreshTokenRequest;
}
 
const resetRefreshTokenRequest = (): void => {
  refreshTokenRequest = null;
}
 
const makeRefreshTokenRequest = async (): Promise<void> => {
  const refreshTokenValue = auth.getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  const refreshTokenData = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshTokenValue
  });
 
  try {
    const response = await axios.post<TokenResponse>(`${API_BASE_URL}/Token`, refreshTokenData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    
    auth.setTokens(response.data.access_token, response.data.refresh_token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
  } catch (error) {
    auth.logOut();
    throw error;
  }
}
 
const createContractUrl = (path: string): string => {
  const contractId = auth.getCurrentContractId();
  return `${API_BASE_URL}${pathPrefix}/Contract/${contractId}${path}`;
}

const createNonContractUrl = (path: string): string => {
  return `${API_BASE_URL}${pathPrefix}${path}`;
}

const getResource = async <T = any>(
  endpoint: string,
  params?: Record<string, any>
): Promise<T> => {
  try {
    const response = await apiClient.get<T>(endpoint, { params });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiError(
        error.response?.data?.message || error.message,
        error.response?.status,
        endpoint
      );
    }
    throw new NetworkError();
  }
};

const createResource = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  try {
    const response = await apiClient.post<T>(endpoint, data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiError(
        error.response?.data?.message || error.message,
        error.response?.status,
        endpoint
      );
    }
    throw new NetworkError();
  }
};

const updateResource = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  try {
    const response = await apiClient.put<T>(endpoint, data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiError(
        error.response?.data?.message || error.message,
        error.response?.status,
        endpoint
      );
    }
    throw new NetworkError();
  }
};

const deleteResource = async <T = any>(endpoint: string): Promise<T> => {
  try {
    const response = await apiClient.delete<T>(endpoint);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiError(
        error.response?.data?.message || error.message,
        error.response?.status,
        endpoint
      );
    }
    throw new NetworkError();
  }
};

class ApiConfig {
  
  getBaseUrl(): string {
    return API_BASE_URL;
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const formData = new URLSearchParams({
        grant_type: 'password',
        username,
        password,
      });

      const response = await fetch(`${API_BASE_URL}/Token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        const data: TokenResponse = await response.json();
        const token = data.access_token;
        const isPasswordTemp = String(data.isPasswordTemp ?? '').toLowerCase() === 'true';
        const isPasswordExpired = String(data.isPasswordExpired ?? '').toLowerCase() === 'true';
        const userInfo = {
          userId: data.userId,
          username: data.username,
          email: data.email,
          roles: data.roles?.split(',') ?? [],
          contractId: data.contractId,
        };

        if (token && (isPasswordTemp || isPasswordExpired)) {
          sessionStorage.setItem('temp_access_token', token);
          sessionStorage.setItem('temp_refresh_token', data.refresh_token);
          sessionStorage.setItem('temp_user_info', JSON.stringify(userInfo));
          return {
            success: true,
            token,
            isPasswordTemp: String(data.isPasswordTemp ?? ''),
            isPasswordExpired: String(data.isPasswordExpired ?? ''),
          };
        }

        if (token) {
          auth.setUserInfo(
            data.userId,
            data.username,
            data.email,
            data.roles,
            data.contractId
          );
          auth.setTokens(data.access_token, data.refresh_token);
          return {
            success: true,
            token,
            isPasswordTemp: String(data.isPasswordTemp ?? ''),
            isPasswordExpired: String(data.isPasswordExpired ?? ''),
          };
        }

        return { success: false, error: 'No token received from server' };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error_description || errorData.message || `Login failed with status: ${response.status}`
        };
      }
    } catch (error) {
      console.error('Login request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred'
      };
    }
  }
 
  async getSignInMessage(): Promise<string> {
    try {
      const response = await apiClient.get(`${pathPrefix}/List/SignInMessage`);
      // Handle the nested message structure from json-server
      if (response.data && typeof response.data.message === 'string') {
        return response.data.message;
      }
      return response.data;
    } catch (error) {
      throw new ApiError('Failed to get sign-in message');
    }
  }
 
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const data = { 
      CurrentPassword: currentPassword, 
      NewPassword: newPassword 
    };
    
    await updateResource(`${API_BASE_URL}${pathPrefix}/Account/Password`, data);
  }
 
  async forgotPassword(data: { email?: string; username?: string }): Promise<void> {
    await createResource(`${API_BASE_URL}${pathPrefix}/Account/ForgotPassword`, data);
  }
 
  // Idle preferences
  async getIdlePreferences(): Promise<any> {
    return getResource(`${API_BASE_URL}${pathPrefix}/Account/IdlePreferences`);
  }
 
  // Contracts
  async getContracts(): Promise<any[]> {
    return getResource(createNonContractUrl('/Account/Contracts'));
  }
 
  async setDefaultContract(contractId: number): Promise<void> {
    await updateResource(`${API_BASE_URL}${pathPrefix}/Account/DefaultContract/${contractId}`);
  }
 
  // User preferences with proper typing
  async getContractUserPreferences(): Promise<UserPreferencesResponse> {
    return getResource<UserPreferencesResponse>(createContractUrl('/Account/UserPreferences'));
  }
 
  async updateContractUserPreferences(data: Partial<UserPreferencesResponse>): Promise<UserPreferencesResponse> {
    return updateResource<UserPreferencesResponse>(createContractUrl('/Account/UserPreferences'), data);
  }
 
  // Contract details
  async getContractDetails(): Promise<any> {
    const contractId = auth.getCurrentContractId();
    return getResource(`${API_BASE_URL}${pathPrefix}/Contract/Details/${contractId}`);
  }

  async getContractLineItems(): Promise<GetLineItemsResponse> {
    return getResource(createContractUrl('/LineItem/Groups'));
  }
  
  async getContractInspectionResults(from?: string, to?: string): Promise<InspectionResultGroupResponse[]> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const query = params.toString();
    return getResource(createContractUrl('/InspectionResults/Groups') + (query ? `?${query}` : ''));
  }
  
  async getContractAreasOfExpertise(): Promise<ExpertiseAreasResponse[]> {
    return getResource(createContractUrl('/List/ExpertiseAreas'));
  }
 
  async getContractSites(): Promise<SitesResponse[]> {
    return getResource(createContractUrl('/List/Sites'));
  }

  async checkLineItemExists(data: LineItemData): Promise<boolean> {
    const params = new URLSearchParams({
      id: data.Id.toString(),
      sectionId: data.SectionId.toString(),
      ...(data.Paragraph && { paragraph: data.Paragraph }),
      ...(data.SubParagraph && { subParagraph: data.SubParagraph }),
      ...(data.Remainder && { remainder: data.Remainder }),
    });

    return getResource<boolean>(`${createContractUrl('/LineItem/CheckLineItemExists')}?${params}`);
  }
 
  // Password management
  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    const data = { currentPassword, newPassword };
    await updateResource(`${API_BASE_URL}${pathPrefix}/Account/ChangePassword`, data);
  }
 
  // User messages
  async getUserMessages(): Promise<string> {
    try {
      const response = await apiClient.get(`${API_BASE_URL}${pathPrefix}/Account/Messages`);
      // Handle the nested message structure from json-server
      if (response.data && typeof response.data.message === 'string') {
        return response.data.message;
      }
      return response.data;
    } catch (error) {
      throw new ApiError('Failed to get user messages');
    }
  }

  async getContractCorrectiveActions(): Promise<any[]> {
    return getResource(createContractUrl(`/CorrectiveActions/Groups`));
  }

  async getContractCorrectiveAction(correctiveActionId: number): Promise<CorrectiveActionLineItemResponse> {
    return getResource(createContractUrl(`/CorrectiveActions/CorrectiveActionFull/${correctiveActionId}`));
  }

  async getContractInspectors(): Promise<any[]> {
    return getResource(createContractUrl('/List/Inspectors'));
  }

  async getContractClassifications(): Promise<any[]> {
    return getResource(createContractUrl('/List/Classifications'));
  }
  
  async getContractUsers(): Promise<any[]> {
    return getResource(createContractUrl('/List/Users'));
  }

  async getContractGroups(): Promise<any[]> {
    return getResource(createContractUrl('/List/Groups'));
  }

  async getContractDashboardToDo(groupIds?: number[]): Promise<DashboardToDoResponse> {
    const params = new URLSearchParams();
    groupIds?.forEach((groupId) => params.append('groupIds', groupId.toString()));

    const endpoint = createContractUrl('/TrendAnalysis/DashboardToDo');
    return getResource<DashboardToDoResponse>(params.toString() ? `${endpoint}?${params.toString()}` : endpoint);
  }

  async getInspectionScheduleLineItems(from: string, to: string): Promise<InspectionScheduleLineItem[]> {
    const params = new URLSearchParams({ from, to });
    return getResource(`${createContractUrl('/InspectionSchedule/LineItems')}?${params}`);
  }

  async getAllInspectionScheduleLineItems(): Promise<InspectionScheduleLineItem[]> {
    return getResource(createContractUrl('/InspectionSchedule/LineItems'));
  }

  async getUserTypes(): Promise<any[]> {
    return getResource(createNonContractUrl('/List/UserTypes'));
  }

  async getInspectionMethods(): Promise<any[]> {
    return getResource(createNonContractUrl('/List/InspectionMethods'));
  }

  async getTransactionTypes(): Promise<any[]> {
    return getResource(createNonContractUrl('/List/TransactionTypes'));
  }

  async getContractFrequencies(): Promise<any[]> {
    return getResource(createContractUrl('/List/Frequencies'));
  }

  async getContractFiles(): Promise<any[]> {
    return getResource(createContractUrl('/File/Files'));
  }
  
  async getContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    return getResource(createContractUrl(`/InspectionResults/Inspection/${inspectionResultId}`));
  }
  
  async getContractInspectionResultsForReport(ids: number[]): Promise<InspectionLineItemResponse[]> {
      let params = ids.map(id => `ids=${id}`).join('&')
      return getResource(createContractUrl('/InspectionResults/Inspections?' + params))
  }

  async getInspectionResultLineItems(groupId: number): Promise<AddInspectionLineItemResponse[]> {
    return getResource(createContractUrl(`/InspectionResults/LineItems/${groupId}`));
  }

  async saveInspection(data: any): Promise<void> {
    return createResource(createContractUrl('/InspectionResults/Inspection'), data);
  }

  async approveInspection(data: any): Promise<void> {
    return createResource(createContractUrl('/InspectionResults/Approve'), data);
  }

  async deleteInspection(data: any): Promise<void> {
    return createResource(createContractUrl('/InspectionResults/DeleteInspection'), data);
  }

  async saveCorrectiveAction(data: any): Promise<number> {
    return createResource(createContractUrl('/CorrectiveActions/CorrectiveAction'), data);
  }

  async deleteCorrectiveAction(data: any): Promise<number> {
    return createResource(createContractUrl('/CorrectiveActions/DeleteCorrectiveAction'), data);
  }

  async getCorrectiveActionResponses(correctiveActionId: number): Promise<any[]> {
    return getResource(createContractUrl(`/CorrectiveActions/Responses/${correctiveActionId}`));
  }

  async saveCorrectiveActionResponse(data: any): Promise<void> {
    return createResource(createContractUrl('/CorrectiveActions/Responses'), data);
  }
 
  // File operations with better typing and error handling
  async uploadFile(file: FormData): Promise<{ FileId: number }> {
    try {
      const response = await apiClient.post(createContractUrl('/File/Upload'), file, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ApiError('File upload failed', error.response?.status);
      }
      throw new NetworkError('File upload failed');
    }
  }
 
  generateFileUrl(fileId: string): string {
    const token = auth.getToken();
    return createContractUrl(`/File/Download/${fileId}?access_token=${encodeURIComponent(token || '')}`);
  }

  generateAttachmentDownloadUrl(fileId: number): string {
    const token = auth.getToken();
    return createContractUrl(`/File/Download/${fileId}?access_token=${encodeURIComponent(token || '')}`);
  }

  generateAttachmentPreviewUrl(attachmentId: number, fileId: number, previewSize: number = 400): string {
    const token = auth.getToken();
    return createContractUrl(`/File/AttachmentFilePreview/${attachmentId}/${fileId}/${previewSize}?access_token=${encodeURIComponent(token || '')}`);
  }

  // Additional utility methods
  async deleteContract(contractId: number): Promise<void> {
    await deleteResource(`${API_BASE_URL}${pathPrefix}/Contract/${contractId}`);
  }

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return getResource<{ status: string; timestamp: string }>(`${API_BASE_URL}${pathPrefix}/health`);
  }

  // Inspection Planning Methods
  async copyPasteInspectionPlan(data: CopyPasteInspectionPlanRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/InspectionPlan/CopyPaste'), data);
    return response;
  }

  async createGroup(data: CreateGroupRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/LineItem/Group'), data);
    return response;
  }

  async updateGroup(data: UpdateGroupRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/LineItem/Group'), data);
    return response;
  }

  async createLineItem(data: CreateLineItemRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/LineItem/LineItem'), data);
    return response;
  }

  async updateLineItem(data: UpdateLineItemRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/LineItem/LineItem'), data);
    return response;
  }

  async activateLineItem(id: number): Promise<number> {
    const body = { Id: id };
    const response = await createResource<number>(createContractUrl('/LineItem/ActivateLineItem'), body);
    return response;
  }

  async inactivateLineItem(id: number, reason: string): Promise<number> {
    const body = { Id: id, reason };
    const response = await createResource<number>(createContractUrl('/LineItem/InactivateLineItem'), body);
    return response;
  }

  async getInspectionPlans(): Promise<GetInspectionPlansResponse> {
    return getResource<GetInspectionPlansResponse>(createContractUrl('/InspectionPlan/Groups'));
  }

  async resetInspectionPlan(inspectionPlanIds: number[]): Promise<number> {
    const response = await createResource<number>(createContractUrl('/InspectionPlan/Reset'), inspectionPlanIds);
    return response;
  }

  async activateDeactivateInspectionPlan(activeIds: number[], inactiveIds: number[]): Promise<number> {
    const body = { Active: activeIds, Inactive: inactiveIds };
    const response = await createResource<number>(createContractUrl('/InspectionPlan/Activate'), body);
    return response;
  }

  async lockUnlockInspectionPlan(lockIds: number[], unlockIds: number[], reason: string): Promise<number> {
    const body = { Lock: lockIds, Unlock: unlockIds, Reason: reason };
    const response = await createResource<number>(createContractUrl('/InspectionPlan/Lock'), body);
    return response;
  }

  async updateInspectionPlan(data: UpdateInspectionPlanRequest): Promise<number> {
    const response = await createResource<number>(createContractUrl('/InspectionPlan/InspectionPlan'), data);
    return response;
  }

  // User Preferences admin methods
  async updateEmailAddress(emailAddress: string): Promise<void> {
    await createResource(`${API_BASE_URL}${pathPrefix}/Account/EmailAddress`, { EmailAddress: emailAddress });
  }

  async changeAccountPassword(currentPassword: string, newPassword: string): Promise<void> {
    await createResource(`${API_BASE_URL}${pathPrefix}/Account/Password`, { currentPassword, newPassword });
  }

  async saveUserPreferences(data: object): Promise<number> {
    return createResource<number>(createContractUrl('/Account/UserPreferences'), data);
  }

  async resetUserPreferences(): Promise<void> {
    await createResource(createContractUrl('/Account/ResetUserPreferences'), null);
  }

  // Module Settings methods
  async getContractImages(): Promise<string[]> {
    return getResource<string[]>(createContractUrl('/Contract/Images'));
  }

  async saveModuleDetails(data: ModuleDetailsRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Name'), data);
  }

  async uploadModuleImage(file: FormData): Promise<void> {
    try {
      await apiClient.post(createContractUrl('/Contract/Image'), file, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ApiError('Image upload failed', error.response?.status);
      }
      throw new NetworkError('Image upload failed');
    }
  }

  async deleteModuleImage(filename: string): Promise<void> {
    await createResource(createContractUrl('/Contract/DeleteImage'), { filename });
  }

  async saveModuleLogo(filename: string): Promise<void> {
    await createResource(createContractUrl('/Contract/Logo'), { filename });
  }

  async saveReportSettings(data: ReportSettingsRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/ReportSettings'), data);
  }

  async saveTransactionLogging(data: TransactionLoggingRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/TransactionLogging'), data);
  }

  async saveScheduleImpact(data: ScheduleImpactRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/ScheduleImpact'), data);
  }

  async saveFrequencyUserTypes(data: FrequencyUserTypesRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/FrequencyUserTypes'), data);
  }

  async saveFrequency(data: SaveFrequencyRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Frequency'), data);
  }

  async saveFrequencyProfile(data: SaveFrequencyProfileRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Profile'), data);
  }

  async saveDateForwarding(data: DateForwardingRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/DateForwarding'), data);
  }

  async saveExpertiseArea(data: SaveAreaRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Area'), data);
  }

  async saveSite(data: SaveSiteRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Site'), data);
  }

  async activateSite(data: ActivateSiteRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/ActivateSite'), data);
  }

  async saveDefaultSite(data: DefaultSiteRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/DefaultSite'), data);
  }

  async saveSaveInProgress(data: SaveInProgressRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/SaveInProgress'), data);
  }

  async saveResultsHighlighting(data: ResultsHighlightingRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/ResultsHighlighting'), data);
  }

  async saveClassification(data: SaveClassificationRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/Classification'), data);
  }

  async activateClassification(data: ActivateClassificationRequest): Promise<void> {
    await createResource(createContractUrl('/Contract/ActivateClassification'), data);
  }

  async getManagementUsers(): Promise<UserManagementUserResponse[]> {
    return getResource(createContractUrl('/UserManagement/Users'));
  }

  async saveUserDetails(data: SaveUserDetailsRequest): Promise<void> {
    await createResource(createContractUrl('/UserManagement/UserDetails'), data);
  }

  async saveUserAssignment(data: SaveUserAssignmentRequest): Promise<void> {
    await createResource(createContractUrl('/UserManagement/UserAssignment'), data);
  }

  async saveUserPrivileges(data: SaveUserPrivilegesRequest): Promise<void> {
    await createResource(createContractUrl('/UserManagement/UserPrivileges'), data);
  }

  async resetUserPassword(data: ResetUserPasswordRequest): Promise<void> {
    await createResource(createNonContractUrl('/UserManagement/UserPassword'), data);
  }

  async saveUserManagementPreferences(data: SaveUserPreferencesRequest): Promise<void> {
    await createResource(createContractUrl('/UserManagement/UserPreferences'), data);
  }

  async getOnlineUsers(): Promise<OnlineUserResponse[]> {
    return getResource(createNonContractUrl('/Enterprise/OnlineUsers'));
  }

  async sendMessage(data: SendMessageRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/Messages'), data);
  }

  async getLicenseInfo(): Promise<LicenseInfoResponse> {
    return getResource(createNonContractUrl('/Enterprise/LicenseInfo'));
  }

  async saveLicenseInfo(data: SaveLicenseInfoRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/LicenseInfo'), data);
  }

  async getEnterprisePreferences(): Promise<EnterprisePreferencesResponse> {
    return getResource(createNonContractUrl('/Enterprise/Preferences'));
  }

  async savePasswordPolicy(data: SavePasswordPolicyRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/Preferences'), data);
  }

  async saveEmailPreferences(data: SaveEmailPreferencesRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/EmailPreferences'), data);
  }

  async getLoginImages(): Promise<LoginImageResponse[]> {
    return getResource(createNonContractUrl('/Enterprise/LoginImages'));
  }

  async uploadLoginImage(file: FormData): Promise<void> {
    try {
      await apiClient.post(createNonContractUrl('/Enterprise/LoginImage'), file, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ApiError('Image upload failed', error.response?.status);
      }
      throw new NetworkError('Image upload failed');
    }
  }

  async deleteLoginImage(data: DeleteLoginImageRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/DeleteLoginImage'), data);
  }

  async saveLoginImageSelection(data: SaveLoginImageSelectionRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/Image'), data);
  }

  async saveSignInMessage(data: SaveSignInMessageRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/SignInMessage'), data);
  }

  async getOfflineAppSettings(): Promise<OfflineAppSettingsResponse> {
    return getResource(createContractUrl('/InspectionResults/GetOfflineInspectionAppSettings'));
  }

  async saveOfflineAppSettings(data: SaveOfflineAppSettingsRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/PostOfflineInspectionAppSettings'), data);
  }

  async addModule(data: AddModuleRequest): Promise<void> {
    await createResource(createNonContractUrl('/Enterprise/NewContract'), data);
  }

  async importContractData(file: FormData, importType: 1 | 2): Promise<void> {
    try {
      await apiClient.post(
        createContractUrl(`/Contract/Import?importType=${importType}`),
        file,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ApiError('Import failed', error.response?.status);
      }
      throw new NetworkError('Import failed');
    }
  }
}
const apiConfig = new ApiConfig();
export { ApiError, NetworkError, apiConfig };
export { refreshToken as refreshTokenNow };

// Called by TenantService on mobile after tenant discovery to point all requests at the selected backend.
export function setBaseUrl(url: string): void {
  API_BASE_URL = url;
  apiClient.defaults.baseURL = url;
}

export default apiConfig;